/**
 * /leaderboard + /bestenliste page glue (leaderboard-dedicated-page-design § The page), plus their
 * /{YYYY-MM} hall-of-fame twins: mounts the
 * shared board module into the server-rendered `<main class="leaderboard-page">`, mode strip on,
 * no module heading (the page's own server-rendered `<h1>` leads). Reveal-when-earned drops for
 * this player-initiated surface — the start-screen section's own gating is untouched. `wirePage
 * (root)` is exported so tests can drive it over fixture DOM (the `multiplayer-page.js` /
 * `games-list-enhance.js` precedent); the bottom-of-file guard calls it for the real page.
 *
 * Client-side `t()` calls (the sort/window/mode chip labels) need the message bundle loaded first
 * — this page has no `boot.js` wiring on its path, so it self-wires the same `initTooltips` +
 * `loadMessages` sequence `games-list-enhance.js` uses for its own self-wired page.
 */
import { loadMessages } from "../i18n.js";
import { initTooltips } from "../tooltip.js";
import { renderLeaderboardInto } from "./leaderboard.js";

/**
 * Wires the page over `root` — finds the `.leaderboard-page` mount and renders the board into it.
 * The two `data-month*` attributes are template-stamped and absent on the live routes, so their
 * presence alone is what tells a hall-of-fame page from the live board here.
 * Exported for tests; the bottom-of-file guard calls it with `document` for the real page.
 */
export function wirePage(root) {
  const mount = root.querySelector(".leaderboard-page");
  const month = mount.dataset.month ?? null;
  // The page's own producer of the leaderboard-inspected `source` dimension
  // (leaderboard-dedicated-page-design § Analytics; hall-of-fame § 7 adds "month") — JS-owned like
  // the start-screen and overlay stamps, rather than a template-static duplicate of the same
  // "this mount is 'page'" knowledge.
  mount.dataset.lbSurface = month ? "month" : "page";
  void renderLeaderboardInto(
    mount,
    { gameMode: "SKAT", playerName: localStorage.getItem("playerName") || "" },
    {
      modeStrip: true,
      heading: false,
      month,
      monthLabel: mount.dataset.monthLabel ?? null,
    },
  );
}

if (
  typeof document !== "undefined" &&
  document.querySelector(".leaderboard-page") != null
) {
  document.addEventListener("DOMContentLoaded", async () => {
    initTooltips(); // self-wired page — boot.js's call never runs here
    try {
      await loadMessages();
    } catch (e) {
      console.warn("[leaderboard-page] message bundle load failed:", e.message);
    }
    wirePage(document);
  });
}
